import type {CanvasData} from '@continuum/ui';
import type {Canvas} from './types';

export function mapSharingCanvasToView(canvas: Canvas): CanvasData {
  const blocks = canvas.blocks.map((block) => {
    const base = {
      id: block.id,
      title: block.title,
      position: block.position,
    };

    switch (block.__typename) {
      case 'TextBlock':
        return {...base, type: 'text' as const, body: block.body};
      case 'MeetingSummaryBlock':
        return {...base, type: 'meeting_summary' as const, body: block.body};
      case 'ListBlock':
        return {
          ...base,
          type: 'list' as const,
          items: block.items.map((item) => ({text: item.text})),
        };
      case 'DocumentsBlock':
        return {
          ...base,
          type: 'documents' as const,
          documents: block.documents.map((doc) => ({
            id: doc.id,
            name: doc.name,
            contentType: doc.contentType,
            byteSize: doc.byteSize,
          })),
        };
      case 'ScheduleMeetingBlock':
        return {
          ...base,
          type: 'schedule_meeting' as const,
          url: block.url,
          subtitle: block.subtitle,
        };
      case 'RichTextBlock':
        return {
          ...base,
          type: 'rich_text' as const,
          body: block.body,
        };
      case 'MetricBlock':
        return {
          ...base,
          type: 'metric' as const,
          metrics: block.metrics.map((m) => ({
            label: m.label,
            value: m.value,
            change: m.change,
            trend: m.trend,
          })),
        };
      case 'ChartBlock':
        return {
          ...base,
          type: 'chart' as const,
          chartType: block.chartType,
          series: block.series.map((s) => ({
            key: s.key,
            color: s.color,
          })),
          data: block.data.map((row) => ({
            name: row.name,
            values: row.values as Record<string, number>,
            formatted: (row.formatted as Record<string, string> | null) ?? null,
          })),
          xAxisLabel: block.xAxisLabel,
          yAxisLabel: block.yAxisLabel,
          description: block.description,
        };
      default:
        return null;
    }
  });

  const validBlocks = blocks.filter(
    (b): b is NonNullable<(typeof blocks)[number]> => b !== null,
  );

  return {
    title: canvas.title,
    organizationName: canvas.organization.name,
    organizationLogoUrl: canvas.organization.logoUrl,
    authorName: canvas.advisor?.name,
    authorEmail: canvas.advisor?.email,
    authorAvatarUrl: canvas.advisor?.profileImageUrl,
    updatedAt: canvas.updatedAt,
    createdAt: canvas.createdAt,
    blocks: validBlocks,
  };
}
